import { useState } from 'react';
import { Alert, Form, Input, Message } from '@arco-design/web-react';
import { changePassword } from '../api/system';
import { PASSWORD_RULES, PASSWORD_RULE_MSG } from '../utils/password';
import { useAuthStore } from '../store/auth';
import FormModal from './FormModal';

/**
 * 全局修改密码 Modal（W5-4）。三个触发源共用：
 * 1. 首次登录 must_change_password=true（BasicLayout 强制打开）；
 * 2. 任意 API 返回 code=4001（request.ts 拦截器强制打开）；
 * 3. 顶栏用户菜单「修改密码」（可关闭）。
 * forced=true 时不可关闭/不可跳过（无取消键、禁遮罩与 Esc），改密成功才放行。
 */
export default function ChangePasswordModal() {
  const [form] = Form.useForm();
  const pwdModal = useAuthStore((s) => s.pwdModal);
  const closePwdModal = useAuthStore((s) => s.closePwdModal);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [saving, setSaving] = useState(false);
  const forced = pwdModal.forced;

  const onOk = async () => {
    const v = await form.validate();
    setSaving(true);
    try {
      await changePassword({ old_password: v.old_password, new_password: v.new_password });
      Message.success('密码修改成功');
      // 更新登录态，解除 must_change_password 强制锁定
      if (user) setUser({ ...user, must_change_password: false });
      form.resetFields();
      closePwdModal();
    } catch {
      /* 拦截器已原样弹出后端错误 */
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      form={form}
      title={forced ? '首次登录须修改密码' : '修改密码'}
      visible={pwdModal.visible}
      onOk={onOk}
      onCancel={closePwdModal}
      confirmLoading={saving}
      maskClosable={!forced}
      escToExit={!forced}
      closable={!forced}
      hideCancel={forced}
    >
      {forced && (
        <Alert
          type="warning"
          style={{ marginBottom: 16 }}
          content="为保障账号安全，请先修改初始密码后再继续使用系统。"
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          field="old_password"
          label="旧密码"
          rules={[{ required: true, message: '请输入旧密码' }]}
        >
          <Input.Password placeholder="请输入当前密码" />
        </Form.Item>
        <Form.Item
          field="new_password"
          label="新密码"
          rules={[
            { required: true, message: '请输入新密码' },
            ...PASSWORD_RULES,
          ]}
        >
          <Input.Password placeholder={PASSWORD_RULE_MSG} />
        </Form.Item>
        <Form.Item
          field="confirm_password"
          label="确认新密码"
          rules={[
            { required: true, message: '请再次输入新密码' },
            {
              validator: (v, cb) => {
                if (v !== form.getFieldValue('new_password')) return cb('两次输入的密码不一致');
                return cb();
              },
            },
          ]}
        >
          <Input.Password placeholder="再次输入新密码" />
        </Form.Item>
      </Form>
    </FormModal>
  );
}
